/**
 * How the visitor came to the landing page, and how the page writes a price.
 *
 * Which line the hero opens with follows the way the visitor came: an ad's
 * (?ad=dinner, ?ad=frost), or the owner's own words to a visitor from her
 * Instagram or Facebook (?from=ig, ?from=fb, or kept from an earlier visit).
 *
 * What the page says about price, the trial, Free and the founding welcome
 * is the server's (GET /billing/landing, shared/api/billing getLandingFacts;
 * architecture audit batch 8): which prices are in force by the server's
 * clock, the founding welcome for the open founding code, the trial's days
 * for a follower, Free's plants, the founding places, the owner's garden
 * address and the plan rows. Nothing here works a date, a price or a phase
 * out; the browser's clock decides none of it.
 *
 * Pure, so it is tested (landingFacts.test.mjs).
 *
 * Who uses it: LandingPage (arrivalOf, isFollower), the Hero and Price
 * sections (usd, usdOf, isFollower), and the Gardens section (isFollower,
 * ownerFirst).
 *
 * @module landingFacts
 */

import { signupFromOf, type SignupFrom } from '../../../shared/lib/arrivalSource.ts';
import type { LandingFacts, Price } from '../../../shared/api/billing.ts';

/** What the landing page says, as the server sends it (shared/api/billing). */
export type { LandingFacts };

/**
 * Which first line the hero opens with: the plain one, the one an ad
 * promised (?ad=dinner, ?ad=frost), or the owner's own words to a visitor
 * from her Instagram ('ig', ?from=ig) or Facebook ('fb', ?from=fb).
 */
export type LandingArrival = 'direct' | 'dinner' | 'frost' | SignupFrom;

/**
 * Which first line the visitor is shown: the owner's link on the address
 * (?from=ig or ?from=fb), else the one this browser came by before (kept, so
 * a follower who comes back without the link still has their page and the
 * trial they will get), else the ad's (?ad=dinner or ?ad=frost); anything
 * else is the plain one.
 *
 * @param search - The address's query, such as "?ad=frost" or "?from=ig".
 * @param kept - The owner's link this browser came by before (arrivalSource keptFrom), if any.
 * @returns The arrival.
 */
export function arrivalOf(search: string, kept: SignupFrom | null = null): LandingArrival {
  try {
    const params = new URLSearchParams(search);
    const from = signupFromOf(params.get('from')) ?? kept;
    if (from) return from;
    const ad = params.get('ad')?.trim().toLowerCase();
    return ad === 'dinner' || ad === 'frost' ? ad : 'direct';
  } catch {
    return kept ?? 'direct';
  }
}

/**
 * Whether the visitor came by the owner's own Instagram or Facebook link: her
 * own words in the hero, her garden first, and the follower trial's days.
 *
 * @param arrival - How the visitor came.
 * @returns True for 'ig' and 'fb'.
 */
export function isFollower(arrival: LandingArrival): arrival is SignupFrom {
  return arrival === 'ig' || arrival === 'fb';
}

/**
 * A list of gardens with the owner's own first, when it is in the list;
 * otherwise the list as it came. The rest keep their order.
 *
 * @param gardens - The gardens, as Gardens on Grow sends them.
 * @param slug - The garden to lead with (its address, without the "@").
 * @returns A new list.
 */
export function ownerFirst<T extends { slug: string }>(gardens: readonly T[], slug: string): T[] {
  const owner = gardens.find((garden) => garden.slug.toLowerCase() === slug.toLowerCase());
  return owner ? [owner, ...gardens.filter((garden) => garden !== owner)] : [...gardens];
}

/**
 * Dollars as the page writes them: "$39", "$7.99" (whole amounts without cents).
 *
 * @param dollars - An amount in US dollars.
 * @returns The amount with its sign.
 */
export function usd(dollars: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: Number.isInteger(dollars) ? 0 : 2,
  }).format(dollars);
}

/**
 * A price from the server (in cents) as the page writes it: "$39", "$7.99".
 *
 * @param price - The server's price.
 * @returns The amount with its sign.
 */
export function usdOf(price: Price): string {
  return usd(price.amount / 100);
}
